// ─────────────────────────────────────────────────────────────
// ConsolePanel — shared bottom-anchored console used by both the
// Output tab and the Python (editor) tab.
//
// Single source of truth for the success/failure indicator + auto
// expand-on-failure / collapse-on-success behavior so the user
// sees identical console UX wherever a run result lives.
//
// Behavior:
//   - Persistent: header is always visible, even when collapsed,
//     so the success/failure indicator never disappears.
//   - Initial state: collapsed (header-only) until a run completes.
//   - Auto-state on result: success → collapsed, failure → expanded.
//   - Resizable via drag handle along the top edge (only while open).
//   - Manual toggle via the unfold/fold button in the header.
//
// The component owns its own open / height state but accepts an
// optional `resultKey` (typically `result.job_id`) so the parent can
// trigger the auto expand/collapse behavior on every new run.
// ─────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import UnfoldMoreOutlinedIcon from "@mui/icons-material/UnfoldMoreOutlined";
import UnfoldLessOutlinedIcon from "@mui/icons-material/UnfoldLessOutlined";
import type { ExecutionResult } from "../../api/client";

// ── Layout constants ─────────────────────────────────────────
export const CONSOLE_MIN_HEIGHT = 80;
export const CONSOLE_DEFAULT_HEIGHT = 200;
export const CONSOLE_EXPANDED_HEIGHT = 400;
export const CONSOLE_COLLAPSED_HEIGHT = 36;

interface ConsolePanelProps {
  /** The most recent execution result. Null = no run yet. */
  result: ExecutionResult | null;
  /**
   * Placeholder shown when there's no run yet. Defaults to a generic
   * "Run a script" message. OutputTab and PythonTab can pass a tab-
   * specific hint if useful.
   */
  emptyMessage?: string;
  /**
   * Optional override for the header label. Defaults to "Console Output".
   */
  label?: string;
}

export function ConsolePanel({
  result,
  emptyMessage = "No output yet. Run a script to see results here.",
  label = "Console Output",
}: ConsolePanelProps) {
  const [consoleHeight, setConsoleHeight] = useState(CONSOLE_DEFAULT_HEIGHT);
  const [consoleOpen, setConsoleOpen] = useState(false);

  // Resize state
  const isDragging = useRef(false);
  const dragStartY = useRef(0);
  const dragStartHeight = useRef(0);

  // Auto-state-on-result: failure expands the panel so the error is
  // visible without an extra click; success collapses so it's out of
  // the way of whatever's above (image viewer, editor, chat).
  useEffect(() => {
    if (!result) return;
    setConsoleOpen(result.status !== "success");
  }, [result?.job_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const maxConsoleHeight = typeof window !== "undefined"
    ? Math.floor(window.innerHeight * 0.8)
    : 600;

  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    dragStartY.current = e.clientY;
    dragStartHeight.current = consoleHeight;

    function onMouseMove(ev: MouseEvent) {
      if (!isDragging.current) return;
      const delta = dragStartY.current - ev.clientY; // drag up = increase height
      const newHeight = Math.max(
        CONSOLE_MIN_HEIGHT,
        Math.min(maxConsoleHeight, dragStartHeight.current + delta),
      );
      setConsoleHeight(newHeight);
    }

    function onMouseUp() {
      isDragging.current = false;
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    }

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }, [consoleHeight, maxConsoleHeight]);

  function toggleConsole() {
    setConsoleOpen((open) => {
      if (!open) {
        setConsoleHeight(CONSOLE_EXPANDED_HEIGHT);
      } else {
        setConsoleHeight(CONSOLE_DEFAULT_HEIGHT);
      }
      return !open;
    });
  }

  // Display content. Prefer the merged `console` field for back-compat
  // (Sam separated stdout/stderr 2026-05-06 but kept `console` as the
  // unified blob). Only falls back to stdout+stderr concatenation if
  // the merged field is genuinely empty.
  const consoleText = result
    ? (result.console && result.console.length > 0
        ? result.console
        : [result.stdout ?? "", result.stderr ?? ""].filter(Boolean).join("\n").trim()
          || "(no output)")
    : emptyMessage;

  function handleCopyConsole() {
    navigator.clipboard.writeText(consoleText).catch(() => {/* ignore */});
  }

  return (
    <>
      {/* Resize handle — only visible/usable while open */}
      {consoleOpen && (
        <Box
          onMouseDown={handleResizeMouseDown}
          sx={{
            height: 4,
            cursor: "row-resize",
            bgcolor: "divider",
            flexShrink: 0,
            "&:hover": { bgcolor: "primary.main" },
            transition: "background-color 150ms",
          }}
          aria-hidden
        />
      )}

      {/* Console panel */}
      <Box
        sx={{
          height: consoleOpen ? consoleHeight : CONSOLE_COLLAPSED_HEIGHT,
          minHeight: consoleOpen ? CONSOLE_MIN_HEIGHT : CONSOLE_COLLAPSED_HEIGHT,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.paper",
          borderTop: "1px solid",
          borderColor: "divider",
          transition: consoleOpen ? "none" : "height 150ms",
          overflow: "hidden",
        }}
      >
        {/* Console header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 1.5,
            py: 0.5,
            borderBottom: consoleOpen ? "1px solid" : "none",
            borderColor: "divider",
            flexShrink: 0,
            gap: 1,
          }}
        >
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ flex: 1, display: "flex", alignItems: "center", gap: 0.75 }}
          >
            {label}
            {result && (
              result.status === "success" ? (
                <Tooltip title="Script completed successfully">
                  <CheckCircleOutlineIcon sx={{ fontSize: 15, color: "success.main", verticalAlign: "middle" }} />
                </Tooltip>
              ) : (
                <Tooltip title={`Script failed${result.exit_code != null ? ` (exit code ${result.exit_code})` : ""}`}>
                  <ErrorOutlineIcon sx={{ fontSize: 15, color: "error.main", verticalAlign: "middle" }} />
                </Tooltip>
              )
            )}
          </Typography>
          <Tooltip title="Copy output">
            <IconButton size="small" onClick={handleCopyConsole} aria-label="Copy console output">
              <ContentCopyOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={consoleOpen ? "Collapse console" : "Expand console"}>
            <IconButton
              size="small"
              onClick={toggleConsole}
              aria-label={consoleOpen ? "Collapse console" : "Expand console"}
            >
              {consoleOpen ? (
                <UnfoldLessOutlinedIcon sx={{ fontSize: 14 }} />
              ) : (
                <UnfoldMoreOutlinedIcon sx={{ fontSize: 14 }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Console text */}
        {consoleOpen && (
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: 1.5,
              fontFamily: '"Cascadia Code", "Fira Code", "Consolas", monospace',
              fontSize: "0.75rem",
              lineHeight: 1.6,
              color: "text.primary",
              bgcolor: "background.default",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {consoleText}
          </Box>
        )}
      </Box>
    </>
  );
}
